import { Box, Grid, TextField, Typography } from '@material-ui/core'
import { Button, CircularLoading, ClickableBox } from 'components/facets'
import { useBuyPriceReferences } from 'lib/hooks/useBuyPriceReferences'
import { useTaxationStore } from 'lib/hooks/useTaxationStore'
import { scaleBuyPricesToBudget } from 'lib/utils'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Competitor, InventoryArticle, PricingStrategy, roundTo2Decimals } from 'shared'
import { CancellationToken } from 'utils/cancellationToken'
import { PricingStrategyBox } from '../PricingSettings/PricingStrategyBox'

interface AutopriceBuyPricesProps {
  articles: InventoryArticle[]
  allArticles: InventoryArticle[]
  pricingStrategies: PricingStrategy[]
  competitors: Competitor[]
  competitionDisabled: boolean
  cancellationToken: CancellationToken
  closeDialog: () => void
  applyAutopriceResultsToWorkingCurrentStock: (updatedStock: InventoryArticle[]) => void
}

export const AutopriceBuyPrices = ({
  articles,
  allArticles,
  pricingStrategies,
  competitors,
  competitionDisabled,
  cancellationToken,
  closeDialog,
  applyAutopriceResultsToWorkingCurrentStock,
}: AutopriceBuyPricesProps): JSX.Element => {
  const { t } = useTranslation()
  const fetchBuyReferences = useBuyPriceReferences()
  const purchaseEvents = useTaxationStore((s) => s.purchaseEvents)

  const [chosenStrategyId, setChosenStrategyId] = useState('')
  const [totalBudget, setTotalBudget] = useState<number | null>(null)
  const [isLoading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)

  const chosenStrategy = pricingStrategies.find(({ _id }) => _id === chosenStrategyId)

  useEffect(() => {
    const purchaseEventId = articles[0]?.purchaseEventId
    if (!purchaseEventId) return
    const pe = purchaseEvents.find((p) => p.id === purchaseEventId)
    if (pe?.price) setTotalBudget(pe.price)
  }, [articles, purchaseEvents])

  const handleBuyPrice = async () => {
    if (!chosenStrategy) return
    setLoading(true)
    setProgress(0)

    try {
      const referenceByArticleId = await fetchBuyReferences({
        articles,
        strategy: chosenStrategy,
        cancellationToken,
        onProgress: setProgress,
      })

      const pricedById: Record<string, InventoryArticle> = {}

      if (totalBudget != null && totalBudget > 0) {
        const scaleInputs: { id: string; refPrice: number; quantity: number }[] = []
        for (const article of articles) {
          const refPrice = referenceByArticleId[article.id!]
          if (refPrice == null) continue
          scaleInputs.push({ id: article.id!, refPrice, quantity: article.quantity ?? 1 })
        }
        const buyPriceById = new Map(
          scaleBuyPricesToBudget(scaleInputs, totalBudget).map((r) => [r.id, r.buyPrice])
        )

        for (const article of articles) {
          const buyPrice = buyPriceById.get(article.id!)
          if (buyPrice == null) continue
          const copy = article.copy()
          copy.buyPriceLock = false
          copy.buyPrice = buyPrice
          pricedById[article.id!] = copy
        }
      } else {
        // No budget: assign reference prices directly as buy prices
        for (const article of articles) {
          const refPrice = referenceByArticleId[article.id!]
          if (refPrice == null) continue
          const copy = article.copy()
          copy.buyPriceLock = false
          copy.buyPrice = roundTo2Decimals(refPrice)
          pricedById[article.id!] = copy
        }
      }

      const merged = allArticles.map((article) => pricedById[article.id!] ?? article)
      applyAutopriceResultsToWorkingCurrentStock(merged)

      closeDialog()
    } finally {
      setLoading(false)
    }
  }

  if (isLoading) {
    return (
      <Box padding={5} width="fit-content" margin="0 auto">
        {progress === 100 ? t('autopriceArticles.finishing') : <CircularLoading value={progress} />}
      </Box>
    )
  }

  if (chosenStrategy) {
    return (
      <Box maxWidth="850px">
        <Box width="fit-content" margin="0 auto" padding={3}>
          <Typography gutterBottom variant="body1">
            {articles.length} {t('autopriceArticles.willBeAutopriced')} <b>{chosenStrategy.name}</b>
          </Typography>
          <p>{t('autopriceArticles.totalBuyBudgetHint')}</p>
          <Box maxWidth="300px">
            <TextField
              label={t('autopriceArticles.totalBuyBudget')}
              type="number"
              fullWidth
              value={totalBudget ?? ''}
              onChange={(e) =>
                setTotalBudget(e.target.value === '' ? null : Number(e.target.value))
              }
              InputProps={{ inputProps: { min: 0, step: 0.01, 'data-testid': 'total-buy-budget' } }}
            />
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" padding={2}>
          <Button
            variant="outlined"
            style={{ marginRight: '16px' }}
            onClick={() => setChosenStrategyId('')}
            color="primary"
          >
            {t('common.back')}
          </Button>
          <Button
            variant="contained"
            disabled={totalBudget !== null && totalBudget <= 0}
            onClick={handleBuyPrice}
            data-testid="calculate-buy-prices-btn"
          >
            {t('autopriceArticles.calculateBuyPrices')}
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box width={'1050px'} maxWidth="100%" padding={{ xs: 1, sm: 3 }}>
      <Typography variant="body1">
        <b>{t('autopriceArticles.chooseStrategy')}:</b>
      </Typography>
      <Grid container>
        {pricingStrategies
          .filter(
            ({ _id, base }) =>
              !_id.includes('bulk') && !(competitionDisabled && base.kind === 'articleData')
          )
          .map((pricingStrategy) => (
            <Grid item xs={12} md="auto" key={pricingStrategy._id}>
              <ClickableBox
                onClick={() => setChosenStrategyId(pricingStrategy._id)}
                width="500px"
                maxWidth="100%"
                data-testid={`strategy-${pricingStrategy._id}`}
              >
                <PricingStrategyBox pricingStrategy={pricingStrategy} competitors={competitors} />
              </ClickableBox>
            </Grid>
          ))}
      </Grid>
    </Box>
  )
}
